import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FIELDS as FF } from "@/lib/fields";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { MessageSquareText, Copy, Check } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Info } from "@/components/Info";

const UNFILLED = /\{[a-z_]+\}/gi;

export function SuggestedMessages({ c, recordId, onNoteAdded }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(null);
  const [pendingList, setPendingList] = useState("");
  const [waOpenLink, setWaOpenLink] = useState(null);
  const stage = c?.fields?.[FF.LEAD_STAGE];

  useEffect(() => {
    if (!stage) return;
    api.get("/message-templates", { params: { stage } }).then((r) => setTemplates(r.data.templates || [])).catch(() => setTemplates([]));
  }, [stage]);

  useEffect(() => {
    api.get(`/cases/${recordId}/whatsapp`).then((r) => setWaOpenLink(r.data.customer_open_link || null)).catch(() => {});
  }, [recordId]);

  useEffect(() => {
    api.get(`/cases/${recordId}/documents-checklist`).then((r) => {
      const apps = r.data.applicants || {};
      const lines = [];
      ["borrower", "coborrower", "company"].forEach((k) => {
        (apps[k] || []).forEach((it) => {
          if (it.status === "missing") lines.push(`• ${it.label}`);
          else if (it.status === "needs_reupload") lines.push(`• ${it.label} (re-upload: ${it.reason || "unclear"})`);
        });
      });
      setPendingList(lines.join("\n"));
    }).catch(() => setPendingList(""));
  }, [recordId]);

  const fill = useMemo(() => {
    const f = c?.fields || {};
    const banks = f[FF.BANK_NAME];
    const firstBank = Array.isArray(banks) ? banks[0] : banks;
    const borrower = (f[FF.BORROWER] || f[FF.NAME] || "").split(" ")[0];
    return {
      name: borrower || "there",
      rm_name: (user?.name || "").split(" ")[0],
      bank: firstBank || "",
      amount: f[FF.LOAN_AMOUNT] ? formatINR(f[FF.LOAN_AMOUNT]) : "",
      tenure: f[FF.LOAN_TENURE] ? `${f[FF.LOAN_TENURE]} years` : "",
      pending_list: pendingList,
    };
  }, [c, user, pendingList]);

  const applyVars = (body) => body.replace(/\{(name|rm_name|bank|amount|tenure|pending_list)\}/g, (m, k) => fill[k] || m);

  if (!templates.length) return null;
  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm" data-testid="suggested-messages">
      <h3 className="text-sm font-medium text-[#333333] flex items-center gap-2 mb-3"><MessageSquareText className="w-4 h-4 text-[#FF4802]" /> Suggested messages <span className="text-xs text-[#6B7280] font-normal">· {stage}</span> <Info k="suggested_msgs" /></h3>
      <div className="flex flex-wrap gap-1.5">
        {templates.map((t) => (
          <button key={t.id} onClick={() => setOpen(t)} data-testid={`template-${t.id}`}
            className="px-3 py-1.5 rounded-full text-xs border border-[#E2E2E2] bg-white text-[#333333] hover:border-[#FF6F38] hover:bg-[#FF4802]/5 transition-colors">{t.name}</button>
        ))}
      </div>
      {open && <MessageDialog tmpl={open} initial={applyVars(open.body)} recordId={recordId} name={fill.name} waOpenLink={waOpenLink} onClose={() => setOpen(null)} onNoteAdded={onNoteAdded} />}
    </div>
  );
}

export function MessageDialog({ tmpl, initial, recordId, name, waOpenLink, onClose, onNoteAdded, header }) {
  const [text, setText] = useState(initial);
  const [copied, setCopied] = useState(false);
  const [askSent, setAskSent] = useState(false);
  const unfilled = (text.match(UNFILLED) || []);
  const canCopy = unfilled.length === 0;

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard blocked */ }
    setCopied(true); setAskSent(true);
    toast.success("Copied to clipboard");
  };
  const copyAndOpen = async () => {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard blocked */ }
    setCopied(true); setAskSent(true);
    toast.success("Copied · opening WhatsApp");
    if (waOpenLink) window.open(waOpenLink, "_blank");
  };
  const markSent = async () => {
    try {
      await api.post(`/cases/${recordId}/note`, { type: "WhatsApp", text: `Sent: ${tmpl.name}` });
      toast.success("Logged to timeline · Last Conversation updated");
      onNoteAdded && onNoteAdded();
      onClose();
    } catch { toast.error("Failed to log"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-2xl truva-popup-shadow max-w-lg" aria-describedby={undefined} data-testid="message-dialog">
        <DialogHeader><DialogTitle className="text-[#333333] font-medium">{tmpl.name}</DialogTitle></DialogHeader>
        {header}
        <Textarea value={text} onChange={(e) => { setText(e.target.value); setCopied(false); setAskSent(false); }} rows={10} data-testid="message-body" className="text-sm border-[#E2E2E2] rounded-lg font-sans" />
        {!canCopy && (
          <p className="text-xs text-[#CA1212]" data-testid="message-unfilled">Fill the highlighted placeholders before copying: {[...new Set(unfilled)].join(", ")}</p>
        )}
        <div className="flex items-center justify-between gap-2">
          <button onClick={copy} disabled={!canCopy} data-testid="message-copy" className="flex items-center gap-1.5 text-sm bg-[#FF4802] text-white px-4 py-2 rounded-lg hover:bg-[#E2521B] disabled:opacity-40 font-medium">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
          </button>
          {waOpenLink && (
            <button onClick={copyAndOpen} disabled={!canCopy} data-testid="message-copy-open" className="flex items-center gap-1.5 text-sm bg-[#159A50] text-white px-4 py-2 rounded-lg hover:bg-[#0f7d40] disabled:opacity-40 font-medium">
              <Copy className="w-4 h-4" /> Copy &amp; open WhatsApp
            </button>
          )}
          {askSent && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[#6B7280]">Sent?</span>
              <button onClick={markSent} data-testid="message-sent-yes" className="px-3 py-1.5 rounded-lg bg-[#159A50] text-white text-xs font-medium">Yes, log it</button>
              <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-[#E2E2E2] text-xs">Not yet</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
